// src/components/SharePointSearch/SharePointDocumentCard.tsx

import * as React from "react";
import { useCallback } from "react";
import { NormalizedSharePointRead } from "../../interfaces/sharepointSearchTypes";
import { FileTypeIcon, ExternalLinkIcon } from "./SharePointIcons";
import { spColors } from "./sharepointSearchStyles";
import { estimatePages } from "../../utils/documentSize";
import { getFileTypeLabel } from "./sharepointSearchUtils";

interface SharePointDocumentCardProps {
  result: NormalizedSharePointRead;
}

const CONTENT_PREVIEW_LENGTH = 200;

/**
 * Preview card for a fetched SharePoint document (Option C).
 * Shows file metadata grid, "In context" badge, and a content preview snippet.
 */
const SharePointDocumentCard: React.FC<SharePointDocumentCardProps> = ({ result }) => {
  const handleOpenLink = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (/^https?:\/\//i.test(result.webUrl)) {
        window.open(result.webUrl, "_blank", "noopener,noreferrer");
      }
    },
    [result.webUrl]
  );

  // Strip metadata header (e.g. "# PDF Document: ...\n**Type**: ...\n---\n")
  // and page markers (e.g. "## Page 1") that the backend prepends to extracted PDF content
  const strippedContent = result.content
    .replace(/^#[^\n]*\n[\s\S]*?---\s*\n/, "")
    .replace(/^##\s+Page\s+\d+\s*\n/gm, "")
    .trimStart();
  const contentPreview =
    strippedContent.length > CONTENT_PREVIEW_LENGTH
      ? strippedContent.slice(0, CONTENT_PREVIEW_LENGTH) + "..."
      : strippedContent;

  const estimatedPages = estimatePages(result.totalContentLength);
  const total = result.totalContentLength || 1;
  const startPercent = Math.min(100, Math.round((result.offset / total) * 100));
  const endPercent = Math.min(
    100,
    Math.round(((result.offset + result.charsReturned) / total) * 100)
  );

  const metadataItems: Array<{ label: string; value: string }> = [
    { label: "Type", value: getFileTypeLabel(result.mimeType) },
    { label: "Pages", value: `~${estimatedPages} page${estimatedPages !== 1 ? "s" : ""}` },
  ];

  return (
    <div
      style={{
        borderRadius: "6px",
        border: `1px solid ${spColors.border}`,
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      {/* Header row: file icon + name + badges */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "10px 12px",
          borderBottom: `1px solid ${spColors.border}`,
        }}
      >
        <FileTypeIcon mimeType={result.mimeType} fileName={result.fileName} />
        <span
          style={{
            flex: 1,
            fontSize: "13px",
            fontWeight: 600,
            color: spColors.foreground,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {result.fileName}
        </span>
        <span
          style={{
            fontSize: "10px",
            fontWeight: 500,
            padding: "2px 8px",
            borderRadius: "4px",
            backgroundColor: spColors.tealBg,
            color: spColors.teal,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          In context
        </span>
        <button
          onClick={handleOpenLink}
          style={{
            background: "none",
            border: "none",
            padding: "2px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            borderRadius: "4px",
            opacity: 0.8,
            flexShrink: 0,
          }}
          aria-label={`Open ${result.fileName} in SharePoint`}
          title="Open in SharePoint"
        >
          <ExternalLinkIcon />
        </button>
      </div>

      {/* Metadata grid */}
      <div style={{ padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: "12px" }}>
        {metadataItems.map((item) => (
          <div key={item.label} style={{ minWidth: "80px" }}>
            <div style={{ fontSize: "10px", color: spColors.mutedForeground, marginBottom: "2px" }}>
              {item.label}
            </div>
            <div style={{ fontSize: "12px", color: spColors.foreground, fontWeight: 500 }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {/* Content loaded progress bar */}
      <div
        style={{
          padding: "0 12px",
          marginBottom: contentPreview ? "8px" : "12px",
        }}
      >
        <div
          style={{
            padding: "8px 10px",
            background: spColors.mutedBg,
            borderRadius: "6px",
            border: `1px solid ${spColors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "10px",
              color: spColors.mutedForeground,
              marginBottom: "4px",
            }}
          >
            <span>Content in context</span>
            <span style={{ fontWeight: 600, color: spColors.headerText }}>
              {startPercent === 0 && endPercent === 100
                ? "100%"
                : `${startPercent}% – ${endPercent}%`}
            </span>
          </div>
          <div
            style={{
              height: "4px",
              background: spColors.border,
              borderRadius: "2px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: `${startPercent}%`,
                width: `${endPercent - startPercent}%`,
                height: "100%",
                background: spColors.teal,
                borderRadius: "2px",
              }}
            />
          </div>
        </div>
      </div>

      {/* Content preview */}
      {contentPreview && (
        <div style={{ padding: "0 12px 12px" }}>
          <div
            style={{
              fontSize: "10px",
              color: spColors.mutedForeground,
              marginBottom: "6px",
              fontWeight: 600,
            }}
          >
            Content Preview
          </div>
          <div
            style={{
              fontSize: "11px",
              lineHeight: "1.5",
              color: spColors.foreground,
              padding: "6px 10px",
              borderLeft: `3px solid ${spColors.tealBorder}`,
              backgroundColor: spColors.quoteBg,
              borderRadius: "0 4px 4px 0",
            }}
          >
            {contentPreview}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharePointDocumentCard;
